import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Save, 
  Send, 
  AlertCircle, 
  CheckCircle2, 
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '@/lib/auth';

export function UserCreateUseCase({ onSuccess }: { onSuccess?: () => void } = {}) {
  const { session } = useAuth();
  const navigate = useNavigate();

  // Form Fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');
  const [detectionObjective, setDetectionObjective] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [dataSource, setDataSource] = useState('Microsoft Sentinel');
  const [logSource, setLogSource] = useState('Windows Security Event ID 4769');
  const [mitreTactic, setMitreTactic] = useState('Credential Access (TA0006)');
  const [mitreTechnique, setMitreTechnique] = useState('Steal or Forge Kerberos Tickets (T1558.003)');
  const [priority, setPriority] = useState('High');
  const [expectedOutcome, setExpectedOutcome] = useState('Immediate high-fidelity security incident generated upon SPN extraction spikes');
  const [comments, setComments] = useState('');
  const [attachments] = useState<string[]>([]);

  // Submission State
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSubmit = async (isDraft: boolean) => {
    try {
      setError(null);
      setSuccessMsg(null);

      if (!title.trim()) {
        setError('Title is mandatory.');
        return;
      }

      if (!isDraft && !detectionObjective.trim()) {
        setError('Detection Objective is mandatory for official submission.');
        return;
      }

      if (isDraft) {
        setSavingDraft(true);
      } else {
        setSubmitting(true);
      }

      const token = session?.access_token || localStorage.getItem('compass_token');
      const payload = {
        title: title.trim(),
        description: description.trim(),
        business_justification: businessJustification.trim(),
        detection_objective: detectionObjective.trim(),
        rule_name: ruleName.trim() || title.trim(),
        rule_description: ruleDescription.trim() || description.trim(),
        data_source: dataSource,
        log_source: logSource,
        mitre_tactic: mitreTactic,
        mitre_technique: mitreTechnique,
        priority,
        expected_outcome: expectedOutcome.trim(),
        comments: comments.trim(),
        attachments,
        is_draft: isDraft
      };

      const res = await fetch('/api/user/use-cases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to process use case');
      }

      if (isDraft) {
        setSuccessMsg(`Draft saved successfully! (Draft ID: ${data.data?.number})`);
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else navigate('/user/use-cases/drafts');
        }, 1200);
      } else {
        setSuccessMsg(`Use Case ${data.data?.number} created successfully and entered Step 1 (CREATED) of the 15-stage workflow!`);
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else navigate('/user/use-cases');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while communicating with the server.');
    } finally {
      setSubmitting(false);
      setSavingDraft(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Top Banner */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className="h-4 w-4 text-blue-500" />
          <span>Step 1: Initiation & Specification Gate</span>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 shadow-sm space-y-6 w-full">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl font-bold text-foreground">Create SOC Use Case</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Specify technical detection rules, telemetry data sources, and MITRE ATT&CK mappings to initiate the 15-stage engineering lifecycle.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(false); }} className="space-y-6">
          {/* SECTION 1: CORE IDENTIFICATION */}
          <div className="space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-blue-500 flex items-center gap-1.5">
              <span>1. Core Use Case Identification</span>
            </h2>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Kerberoasting Attack Detection - SPN Request Spikes"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!ruleName) setRuleName(e.target.value);
                }}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Rule Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Detect-Kerberoast-SPN-Anomalies"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                >
                  <option value="Urgent">Urgent / P1 (1-Day Dev SLA)</option>
                  <option value="High">High / P2 (3-Day Dev SLA)</option>
                  <option value="Medium">Medium / P3</option>
                  <option value="Low">Low / P4</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Description
              </label>
              <textarea
                rows={3}
                placeholder="Provide a comprehensive operational description of this detection scenario..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* SECTION 2: OBJECTIVES & JUSTIFICATION */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-500 flex items-center gap-1.5">
              <span>2. Objective & Justification</span>
            </h2>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Detection Objective <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Identify adversaries requesting TGS service tickets for offline Kerberos cracking"
                value={detectionObjective}
                onChange={(e) => setDetectionObjective(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Business Justification
              </label>
              <textarea
                rows={2}
                placeholder="State risk profile, compliance mandates, or threat intelligence reasoning..."
                value={businessJustification}
                onChange={(e) => setBusinessJustification(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Rule Description / Query Specification
              </label>
              <textarea
                rows={2}
                placeholder="Detection algorithm, threshold count, statistical baseline details..."
                value={ruleDescription}
                onChange={(e) => setRuleDescription(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* SECTION 3: TELEMETRY & MITRE ATT&CK */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h2 className="text-xs font-bold uppercase tracking-wider text-purple-500 flex items-center gap-1.5">
              <span>3. Telemetry Sources & MITRE ATT&CK</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Data Source
                </label>
                <input
                  type="text"
                  placeholder="e.g. Microsoft Sentinel, Splunk, CrowdStrike"
                  value={dataSource}
                  onChange={(e) => setDataSource(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Log Source (Required Telemetry)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Windows Security Event ID 4769, Sysmon Event 1"
                  value={logSource}
                  onChange={(e) => setLogSource(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  MITRE Tactic
                </label>
                <input
                  type="text"
                  placeholder="e.g. Credential Access (TA0006)"
                  value={mitreTactic}
                  onChange={(e) => setMitreTactic(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  MITRE Technique
                </label>
                <input
                  type="text"
                  placeholder="e.g. Steal or Forge Kerberos Tickets (T1558.003)"
                  value={mitreTechnique}
                  onChange={(e) => setMitreTechnique(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Expected Outcome / Alert Fidelity
              </label>
              <input
                type="text"
                placeholder="e.g. Generate high-severity incident with affected account and IP entities"
                value={expectedOutcome}
                onChange={(e) => setExpectedOutcome(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* SECTION 4: ATTACHMENTS & COMMENTS */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <span>4. Additional Information</span>
            </h2>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Initiation Comments
              </label>
              <textarea
                rows={2}
                placeholder="Initial comments for Logesh & Heman (Step 2 Shift Lead validation)..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* SUBMIT BUTTONS */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={savingDraft || submitting}
              className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl border border-border bg-card hover:bg-muted text-foreground transition-all disabled:opacity-50"
            >
              <Save className="h-4 w-4 text-muted-foreground" />
              <span>{savingDraft ? 'Saving Draft...' : 'Save Draft'}</span>
            </button>

            <button
              type="submit"
              disabled={submitting || savingDraft}
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/25 transition-all disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              <span>{submitting ? 'Submitting Use Case...' : 'Submit Use Case'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
